import { create } from "zustand"

import { rhodesEventVariants } from "@/data/regions/rhodes"
import { unlockCollection } from "@/services/core"
import type { MapEdge, MapNode, MapRegion, RegionId } from "@/types"
import { collectStageStart } from "@/utils/event"
import { requireLogin, useGlobalStore } from "./global"

export type RhodesVariant = "a" | "b"

interface MapState {
    currentRegionId: RegionId | null
    currentNodeId: string | null
    previousNodeId: string | null
    visitedNodeIds: string[]
    isMoving: boolean
    regions: MapRegion[]
    rhodesVariant: RhodesVariant | null

    initMap: (regionId: RegionId) => void
    moveToNode: (nodeId: string) => void
    setMoving: (moving: boolean) => void
    getReachableNodes: () => MapNode[]
    setRegions: (regions: MapRegion[]) => void
    getCurrentRegion: () => MapRegion | undefined
    getEdgeBetween: (fromId: string, toId: string) => MapEdge | undefined
    unlockNextRegion: () => MapRegion | undefined
}

export const useMapStore = create<MapState>((set, get) => ({
    currentRegionId: null,
    currentNodeId: null,
    previousNodeId: null,
    visitedNodeIds: [],
    isMoving: false,
    regions: [],
    rhodesVariant: null,

    setRegions: (regions) => {
        const mapCollection = useGlobalStore.getState().player?.collection?.map
        if (mapCollection) {
            const unlockedKeys = new Set(mapCollection.map((item) => item.key))
            regions = regions.map((r) =>
                unlockedKeys.has(r.id) ? { ...r, unlocked: true } : r,
            )
        }
        set({ regions })
    },

    initMap: (regionId) => {
        let { regions } = get()
        let rhodesVariant = get().rhodesVariant

        if (regionId === "babel_rhodes") {
            rhodesVariant = Math.random() < 0.5 ? "a" : "b"
            regions = regions.map((r) => {
                if (r.id !== "babel_rhodes") return r
                return {
                    ...r,
                    nodes: r.nodes.map((node) => {
                        const v = rhodesEventVariants[node.id]
                        return v
                            ? { ...node, eventId: v[rhodesVariant!] }
                            : node
                    }),
                }
            })
            set({ regions, rhodesVariant })
        }

        const region = regions.find((r) => r.id === regionId)
        if (!region) return
        set({
            currentRegionId: regionId,
            currentNodeId: region.startNodeId,
            previousNodeId: region.startNodeId,
            visitedNodeIds: [region.startNodeId],
            isMoving: false,
        })

        const { gameStartTs } = useGlobalStore.getState()
        collectStageStart(gameStartTs, regionId)
    },

    moveToNode: (nodeId) => {
        set((state) => ({
            previousNodeId: state.currentNodeId,
            currentNodeId: nodeId,
            visitedNodeIds: [...state.visitedNodeIds, nodeId],
            isMoving: true,
        }))
    },

    setMoving: (moving) => {
        set({ isMoving: moving })
    },

    getReachableNodes: () => {
        const { currentRegionId, currentNodeId, visitedNodeIds, regions } =
            get()
        if (!currentRegionId || !currentNodeId) return []

        const region = regions.find((r) => r.id === currentRegionId)
        if (!region) return []

        const currentNode = region.nodes.find((n) => n.id === currentNodeId)
        if (!currentNode) return []

        const connectedIds = region.edges
            .filter((e) => e.from === currentNodeId || e.to === currentNodeId)
            .map((e) => (e.from === currentNodeId ? e.to : e.from))

        return region.nodes.filter(
            (n) =>
                connectedIds.includes(n.id) &&
                n.level >= currentNode.level &&
                !visitedNodeIds.includes(n.id),
        )
    },

    getCurrentRegion: () => {
        const { currentRegionId, regions } = get()
        return regions.find((r) => r.id === currentRegionId)
    },

    getEdgeBetween: (fromId, toId) => {
        const region = get().getCurrentRegion()
        if (!region) return undefined
        return region.edges.find(
            (e) =>
                (e.from === fromId && e.to === toId) ||
                (e.from === toId && e.to === fromId),
        )
    },

    unlockNextRegion: () => {
        if (!requireLogin()) return undefined

        const { regions } = get()
        const current = get().getCurrentRegion()
        if (!current) return undefined

        const nextRegion = regions
            .filter((r) => r.order > current.order)
            .sort((a, b) => a.order - b.order)[0]

        if (nextRegion && !nextRegion.unlocked) {
            set({
                regions: regions.map((r) =>
                    r.id === nextRegion.id ? { ...r, unlocked: true } : r,
                ),
            })

            const { gameStartTs, updateCollectionGroup } =
                useGlobalStore.getState()
            unlockCollection(gameStartTs, "map", nextRegion.id).then((res) => {
                if (res.code === 0) {
                    updateCollectionGroup("map", res.data.list)
                }
            })
        }

        return nextRegion
    },
}))
